/**
 * KanbanBoard — Auftrags-Kanban mit Backend-Anbindung (B5)
 * Drag & Drop via HTML5 DnD API
 * Quantum90 Design
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import './KanbanBoard.css';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// --- Typen ---

interface AuftragKunde {
  partner_id: number;
  name: string;
  typ: string;
}

interface Auftrag {
  auftrag_id: number;
  auftrag_nr: string;
  kunde_id: number;
  status: string;
  liefertermin: string | null;
  prioritaet: number;
  notizen: string | null;
  erstellt_am: string | null;
  gesamtsumme?: number;
  kunde: AuftragKunde | null;
}

interface KanbanColumn {
  id: string;         // API-Status-Key
  label: string;
  color: string;
  auftraege: Auftrag[];
}

// --- Konfiguration: Status-Spalten ---
//
// Die Spalten werden dynamisch aus GET /api/auftraege/status-optionen aufgebaut
// (branchenabhängig: Produktion/Handwerk/Handel/Projekt liefern unterschiedliche
// Status-Werte in Prozessreihenfolge). Spalten-id UND -label sind der deutsche
// Status-Value selbst — der ist zugleich der Wert von auftrag.status, es ist also
// kein Enum-Namen-Mapping mehr nötig. STORNIERT wird immer ans Ende sortiert.
// Schlägt der Aufruf fehl, dienen die bisherigen 6 PRODUKTION-Stati als Fallback.

const FALLBACK_STATUS_WERTE = [
  'Angebot', 'Bestätigt', 'In Produktion', 'Versandbereit', 'Geliefert', 'Storniert',
];

const STATUS_FARBEN: Record<string, string> = {
  'Angebot':             'var(--bone-3)',
  'Bestätigt':           'var(--green-900)',
  'In Produktion':       'var(--gold)',
  'Versandbereit':       'var(--green-900)',
  'Teilgeliefert':       'var(--gold)',
  'Geliefert':           'var(--green-500)',
  'Beauftragt':          'var(--green-900)',
  'Material disponiert': 'var(--gold)',
  'In Ausführung':       'var(--gold)',
  'Abgenommen':          'var(--green-900)',
  'Fakturiert':          'var(--green-500)',
  'Kommissioniert':      'var(--gold)',
  'In Bearbeitung':      'var(--gold)',
  'Storniert':           'var(--ink-2)',
};

const STATUS_FARBE_DEFAULT = 'var(--bone-3)';

function baueStatusSpalten(werte: string[]): Omit<KanbanColumn, 'auftraege'>[] {
  // STORNIERT (case-insensitiv) ans Ende, Reihenfolge sonst wie vom Backend geliefert
  const sortiert = [...werte].sort((a, b) => {
    const aStorniert = a.toLowerCase() === 'storniert' ? 1 : 0;
    const bStorniert = b.toLowerCase() === 'storniert' ? 1 : 0;
    return aStorniert - bStorniert;
  });
  return sortiert.map(wert => ({
    id: wert,
    label: wert,
    color: STATUS_FARBEN[wert] ?? STATUS_FARBE_DEFAULT,
  }));
}

function prioritaetLabel(p: number): { label: string; cls: string } {
  if (p >= 8) return { label: 'Hoch',    cls: 'hoch' };
  if (p >= 5) return { label: 'Mittel',  cls: 'mittel' };
  return        { label: 'Niedrig', cls: 'niedrig' };
}

function formatEuro(n: number | undefined | null): string {
  if (n === undefined || n === null) return '–';  // ohne preise_sehen liefert das Backend keine Summe
  return n.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 0 });
}

function formatDatum(iso: string | null): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('de-DE'); }
  catch { return iso; }
}

// --- KanbanCard ---

interface CardProps {
  auftrag: Auftrag;
  onDragStart: (auftragId: number) => void;
}

const KanbanCard: React.FC<CardProps> = ({ auftrag, onDragStart }) => {
  const prio = prioritaetLabel(auftrag.prioritaet);
  const istUeberfaellig = auftrag.liefertermin
    ? new Date(auftrag.liefertermin) < new Date()
    : false;

  return (
    <div
      className={`kb-card kb-card--prio-${prio.cls}`}
      draggable
      onDragStart={() => onDragStart(auftrag.auftrag_id)}
    >
      <div className="kb-card-header">
        <span className="kb-card-id">{auftrag.auftrag_nr}</span>
        <span className={`kb-card-prio kb-card-prio--${prio.cls}`}>{prio.label}</span>
      </div>
      <div className="kb-card-title">
        {auftrag.notizen ? auftrag.notizen : `Auftrag ${auftrag.auftrag_nr}`}
      </div>
      <div className="kb-card-meta">
        <span className="kb-card-customer">{auftrag.kunde?.name ?? '—'}</span>
        {auftrag.gesamtsumme !== undefined && (
          <span className="kb-card-amount">{formatEuro(auftrag.gesamtsumme)}</span>
        )}
      </div>
      {auftrag.liefertermin && (
        <div className={`kb-card-due${istUeberfaellig ? ' kb-card-due--overdue' : ''}`}>
          Fällig: {formatDatum(auftrag.liefertermin)}
          {istUeberfaellig && <span className="kb-overdue-tag">Überfällig</span>}
        </div>
      )}
    </div>
  );
};

// --- KanbanBoard ---

interface KanbanBoardProps {
  authToken?: string;
}

const KanbanBoard: React.FC<KanbanBoardProps> = ({ authToken }) => {
  const [columns, setColumns] = useState<KanbanColumn[]>(
    baueStatusSpalten(FALLBACK_STATUS_WERTE).map(col => ({ ...col, auftraege: [] }))
  );
  const [loading, setLoading] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const [dragAuftragId, setDragAuftragId] = useState<number | null>(null);
  const [dragUeber, setDragUeber] = useState<string | null>(null);
  const [statusMeldung, setStatusMeldung] = useState<string | null>(null);
  const statusTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};

  const ladeAuftraege = useCallback(async () => {
    setLoading(true);
    setFehler(null);

    // Status-Spalten branchenabhängig vom Backend holen (Produktion/Handwerk/Handel/
    // Projekt haben unterschiedliche Prozess-Stati). Schlägt das fehl, bleiben die
    // bisherigen 6 PRODUKTION-Spalten als Fallback erhalten.
    let spalten = baueStatusSpalten(FALLBACK_STATUS_WERTE);
    try {
      const optRes = await axios.get<{ status: string[]; auftrag_typ?: string }>(
        `${API_URL}/api/auftraege/status-optionen`, { headers }
      );
      if (optRes.data?.status?.length) {
        spalten = baueStatusSpalten(optRes.data.status);
      }
    } catch (err) {
      console.error('Status-Optionen konnten nicht geladen werden, nutze Standard-Spalten', err);
    }

    try {
      const res = await axios.get<Auftrag[]>(`${API_URL}/api/auftraege`, { headers });
      const auftraege = res.data;

      // Spalten-id ist der deutsche Status-Value selbst — direkter Match auf auftrag.status.
      setColumns(spalten.map(col => ({
        ...col,
        auftraege: auftraege.filter(a => a.status === col.id),
      })));
    } catch (err: unknown) {
      const msg = axios.isAxiosError(err)
        ? (err.response?.data?.detail ?? err.message)
        : 'Unbekannter Fehler';
      setFehler(`Aufträge konnten nicht geladen werden: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    ladeAuftraege();
  }, [ladeAuftraege]);

  // --- Drag & Drop Handlers ---

  const handleDragStart = (auftragId: number) => {
    setDragAuftragId(auftragId);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    setDragUeber(columnId);
  };

  const handleDragLeave = () => {
    setDragUeber(null);
  };

  const handleDrop = async (e: React.DragEvent, zielStatus: string) => {
    e.preventDefault();
    setDragUeber(null);

    // Capture before clearing state to avoid stale closure in rollback
    const draggedId = dragAuftragId;
    setDragAuftragId(null);

    if (draggedId === null) return;

    // Quell-Status bestimmen
    const quellColumn = columns.find(col =>
      col.auftraege.some(a => a.auftrag_id === draggedId)
    );
    if (!quellColumn || quellColumn.id === zielStatus) {
      return;
    }

    // Optimistisches Update
    const auftrag = quellColumn.auftraege.find(a => a.auftrag_id === draggedId)!;
    setColumns(prev => prev.map(col => {
      if (col.id === quellColumn.id) {
        return { ...col, auftraege: col.auftraege.filter(a => a.auftrag_id !== draggedId) };
      }
      if (col.id === zielStatus) {
        // Spalten-id ist der deutsche Status-Value selbst — kein Label-Lookup mehr nötig.
        return { ...col, auftraege: [...col.auftraege, { ...auftrag, status: zielStatus }] };
      }
      return col;
    }));

    // API-Call
    try {
      await axios.put(
        `${API_URL}/api/auftraege/${draggedId}/status`,
        { status: zielStatus },
        { headers }
      );
      zeigeStatusMeldung(`Status geändert zu ${zielStatus}`);
    } catch (err: unknown) {
      // Rollback using draggedId (not dragAuftragId which is now null)
      setColumns(prev => prev.map(col => {
        if (col.id === zielStatus) {
          return { ...col, auftraege: col.auftraege.filter(a => a.auftrag_id !== draggedId) };
        }
        if (col.id === quellColumn.id) {
          return { ...col, auftraege: [...col.auftraege, auftrag] };
        }
        return col;
      }));
      // 409 = Übergang laut zentraler Statusmaschine nicht erlaubt (auftrag_status_map.py) —
      // Backend liefert im detail-Feld eine Meldung inkl. erlaubter nächster Schritte.
      if (axios.isAxiosError(err) && err.response?.status === 409) {
        zeigeStatusMeldung(err.response.data?.detail || 'Statusänderung nicht erlaubt', 6000);
      } else {
        const msg = axios.isAxiosError(err) ? (err.response?.data?.detail ?? err.message) : undefined;
        zeigeStatusMeldung(msg ? `Statusänderung fehlgeschlagen: ${msg}` : 'Statusänderung fehlgeschlagen', 6000);
      }
    }
  };

  const zeigeStatusMeldung = (text: string, dauerMs: number = 3000) => {
    setStatusMeldung(text);
    if (statusTimerRef.current) clearTimeout(statusTimerRef.current);
    statusTimerRef.current = setTimeout(() => setStatusMeldung(null), dauerMs);
  };

  const gesamtAuftraege = columns.reduce((s, col) => s + col.auftraege.length, 0);

  // --- Render ---

  return (
    <div className="kb-container">
      <div className="kb-header">
        <div className="kb-header-top">
          <div>
            <h2>Auftrags-Kanban</h2>
            {!loading && !fehler && (
              <p className="kb-subtitle">
                {gesamtAuftraege} {gesamtAuftraege === 1 ? 'Auftrag' : 'Aufträge'} — per Drag &amp; Drop Status ändern
              </p>
            )}
          </div>
          <button className="kb-reload-btn" onClick={ladeAuftraege} title="Neu laden" disabled={loading}>
            &#8635;
          </button>
        </div>
      </div>

      {loading && (
        <div className="kb-loading">
          <div className="kb-spinner" />
          <span>Aufträge werden geladen...</span>
        </div>
      )}

      {fehler && (
        <div className="kb-fehler">
          <span>{fehler}</span>
          <button onClick={ladeAuftraege}>Erneut versuchen</button>
        </div>
      )}

      {!loading && !fehler && (
        <div className="kb-board">
          {columns.map(col => (
            <div
              key={col.id}
              className={`kb-column${dragUeber === col.id ? ' kb-column--drag-over' : ''}`}
              onDragOver={e => handleDragOver(e, col.id)}
              onDragLeave={handleDragLeave}
              onDrop={e => handleDrop(e, col.id)}
            >
              <div className="kb-column-header" style={{ borderTopColor: col.color }}>
                <span className="kb-column-label">{col.label}</span>
                <span className="kb-column-count">{col.auftraege.length}</span>
              </div>
              <div className="kb-column-body">
                {col.auftraege.map(auftrag => (
                  <KanbanCard
                    key={auftrag.auftrag_id}
                    auftrag={auftrag}
                    onDragStart={handleDragStart}
                  />
                ))}
                {col.auftraege.length === 0 && (
                  <div className="kb-empty-col">Keine Aufträge</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {statusMeldung && (
        <div className="kb-status-toast">{statusMeldung}</div>
      )}
    </div>
  );
};

export default KanbanBoard;
